import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { PRODUCT_QUANTITY_LIMIT, productQuantityLimit } from '@theme/product-quantity-limit';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    const productAllowance = Math.max(
      0,
      PRODUCT_QUANTITY_LIMIT - productQuantityLimit.getTotal(this.productId) + this.currentLineQuantity
    );
    return max === null ? productAllowance : Math.min(max, productAllowance);
  }

  /**
   * Updates button states based on current value and limits
   * Cart limit states are managed client-side while server-disabled states are preserved.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const maxReached = effectiveMax !== null && value >= effectiveMax;

    // Keep merchant-authored unavailable states truly disabled.
    minusButton.disabled = this.serverDisabledMinus || value <= min;
    plusButton.disabled = this.serverDisabledPlus;

    // At the cart limit the plus button remains operable so activating it can
    // reveal the existing quantity-limit message without changing the quantity.
    if (this.serverDisabledPlus) {
      plusButton.removeAttribute('aria-disabled');
    } else {
      plusButton.setAttribute('aria-disabled', maxReached ? 'true' : 'false');
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
